import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { DrawerMode, DrawerPosition } from './drawer.types';

/**
 * Drawer Component
 *
 * A foundational overlay/push panel component that handles positioning,
 * animations, backdrop, and responsive behavior. Can be positioned on
 * the right side of the viewport.
 *
 * @example
 * ```html
 * <zag-drawer [open]="isOpen" [mode]="'push'" [width]="'320px'">
 *   <p>Drawer content here</p>
 * </zag-drawer>
 * ```
 */
@Component({
  selector: 'zag-drawer',
  imports: [],
  template: `
    <!-- Backdrop for overlay mode -->
    @if (showBackdropComputed() && open()) {
      <div class="zag-drawer__backdrop" (click)="onBackdropClick()" [attr.aria-hidden]="true"></div>
    }

    <!-- Drawer panel -->
    <div
      class="zag-drawer__panel"
      [class.zag-drawer__panel--open]="open()"
      [style.width]="effectiveWidth()"
      role="complementary"
      [attr.aria-hidden]="!open()"
    >
      <ng-content />
    </div>
  `,
  styleUrl: './drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-drawer]': 'true',
    '[class.zag-drawer--open]': 'open()',
    '[class.zag-drawer--push]': 'mode() === "push"',
    '[class.zag-drawer--overlay]': 'mode() === "overlay"',
    '[class.zag-drawer--right]': 'position() === "right"',
    '[class.zag-drawer--left]': 'position() === "left"',
    '[class.zag-drawer--mobile]': 'isMobile()',
    '[class.zag-drawer--plain]': 'plain()',
    '[style.--drawer-width]': 'effectiveWidth()',
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class DrawerComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  // Inputs
  readonly open = input<boolean>(false);
  readonly mode = input<DrawerMode>('push');
  readonly position = input<DrawerPosition>('right');
  readonly width = input<string>('320px');
  readonly mobileWidth = input<string>('100%');
  readonly showBackdrop = input<boolean>(true);
  readonly plain = input<boolean>(false);

  // Outputs
  readonly openChange = output<boolean>();
  readonly backdropClick = output<void>();

  // Reactive breakpoint signal - detects if we're on mobile/small screens
  readonly isMobile = toSignal(
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map(result => result.matches)),
    { initialValue: false }
  );

  // Computed: Show backdrop only in overlay mode or on mobile
  readonly showBackdropComputed = computed(() => {
    return this.showBackdrop() && this.mode() === 'overlay';
  });

  // Computed: Effective width with mobile override support
  readonly effectiveWidth = computed(() => {
    return this.isMobile() ? this.mobileWidth() : this.width();
  });

  protected onEscapeKey(): void {
    if (this.open()) {
      this.openChange.emit(false);
    }
  }

  onBackdropClick(): void {
    this.backdropClick.emit();
    this.openChange.emit(false);
  }
}
